import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import * as moment from 'moment';

// tslint:disable-next-line:max-line-length
export type Control<T extends any> = T extends boolean | string | number ? TypeSafeFormControl<T> : T extends any[] ? TypeSafeFormArray<T> : T extends object ? TypeSafeFormGroup<T> : never;

export class TypeSafeFormGroup<T extends object> extends UntypedFormGroup {

  controls: { [K in keyof T]: Control<T[K]>; };

  readonly value: T;

  readonly valueChanges: Observable<T>;

  /* tslint:disable:max-line-length */
  get<K extends keyof T>(k: K): Control<T[K]>;
  get<K extends keyof T, K2 extends keyof T[K]>(k: K, k2: K2): Control<T[K][K2]>;
  get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2]>(k: K, k2: K2, k3: K3): Control<T[K][K2][K3]>;
  get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3]>(k: K, k2: K2, k3: K3, k4: K4): Control<T[K][K2][K3][K4]>;
  get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5): Control<T[K][K2][K3][K4][K5]>;
  // get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6): Control<T[K][K2][K3][K4][K5][K6]>;
  // get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5], K7 extends keyof T[K][K2][K3][K4][K5][K6]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6, k7: K7): Control<T[K][K2][K3][K4][K5][K6][K7]>;
  // get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5], K7 extends keyof T[K][K2][K3][K4][K5][K6], K8 extends keyof T[K][K2][K3][K4][K5][K6][K7]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6, k7: K7, k8: K8): Control<T[K][K2][K3][K4][K5][K6][K7][K8]>;
  // get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5], K7 extends keyof T[K][K2][K3][K4][K5][K6], K8 extends keyof T[K][K2][K3][K4][K5][K6][K7], K9 extends keyof T[K][K2][K3][K4][K5][K6][K7][K8]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6, k7: K7, k8: K8, k9: K9): Control<T[K][K2][K3][K4][K5][K6][K7][K8][K9]>;
  // get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5], K7 extends keyof T[K][K2][K3][K4][K5][K6], K8 extends keyof T[K][K2][K3][K4][K5][K6][K7], K9 extends keyof T[K][K2][K3][K4][K5][K6][K7][K8], K10 extends keyof T[K][K2][K3][K4][K5][K6][K7][K8][K9]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6, k7: K7, k8: K8, k9: K9, k10: K10): Control<T[K][K2][K3][K4][K5][K6][K7][K8][K9][K10]>;
  /* tslint:enable:max-line-length */

  /** @deprecated **/
  get(...path: Array<string | number>): AbstractControl {
    const [pathEl] = path;
    if (pathEl && pathEl.toString().includes('.')) {
      throw Error(`Path string cannot include dot character: ${pathEl}`);
    }
    return super.get(path);
  }

  setValue(value: T, options?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.setValue(value, options);
  }

  patchValue(value: T, options?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.patchValue(value, options);
  }

  reset(value?: T, options?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.reset(value, options);
  }

  getRawValue(): T {
    return super.getRawValue();
  }

}

export class TypeSafeFormArray<T extends any[]> extends UntypedFormArray {

  controls: Control<T[any]>[];

  readonly value: T;

  readonly valueChanges: Observable<T>;

  setValue(value: T, options?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.setValue(value, options);
  }

  patchValue(value: T, options?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.patchValue(value, options);
  }

  reset(value?: T, options?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.reset(value, options);
  }

  at(index: number): Control<T[any]> {
    return super.at(index) as any;
  }

  push(control: Control<T[any]>): void {
    super.push(control);
  }

  getRawValue(): T {
    return super.getRawValue() as T;
  }

  /* tslint:disable:max-line-length */
  get<K extends keyof T>(k: K): Control<T[K]>;
  get<K extends keyof T, K2 extends keyof T[K]>(k: K, k2: K2): Control<T[K][K2]>;
  get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2]>(k: K, k2: K2, k3: K3): Control<T[K][K2][K3]>;
  get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3]>(k: K, k2: K2, k3: K3, k4: K4): Control<T[K][K2][K3][K4]>;
  get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5): Control<T[K][K2][K3][K4][K5]>;
  // get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6): Control<T[K][K2][K3][K4][K5][K6]>;
  // get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5], K7 extends keyof T[K][K2][K3][K4][K5][K6]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6, k7: K7): Control<T[K][K2][K3][K4][K5][K6][K7]>;
  // get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5], K7 extends keyof T[K][K2][K3][K4][K5][K6], K8 extends keyof T[K][K2][K3][K4][K5][K6][K7]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6, k7: K7, k8: K8): Control<T[K][K2][K3][K4][K5][K6][K7][K8]>;
  // get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5], K7 extends keyof T[K][K2][K3][K4][K5][K6], K8 extends keyof T[K][K2][K3][K4][K5][K6][K7], K9 extends keyof T[K][K2][K3][K4][K5][K6][K7][K8]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6, k7: K7, k8: K8, k9: K9): Control<T[K][K2][K3][K4][K5][K6][K7][K8][K9]>;
  // get<K extends keyof T, K2 extends keyof T[K], K3 extends keyof T[K][K2], K4 extends keyof T[K][K2][K3], K5 extends keyof T[K][K2][K3][K4], K6 extends keyof T[K][K2][K3][K4][K5], K7 extends keyof T[K][K2][K3][K4][K5][K6], K8 extends keyof T[K][K2][K3][K4][K5][K6][K7], K9 extends keyof T[K][K2][K3][K4][K5][K6][K7][K8], K10 extends keyof T[K][K2][K3][K4][K5][K6][K7][K8][K9]>(k: K, k2: K2, k3: K3, k4: K4, k5: K5, k6: K6, k7: K7, k8: K8, k9: K9, k10: K10): Control<T[K][K2][K3][K4][K5][K6][K7][K8][K9][K10]>;
  /* tslint:enable:max-line-length */

  /** @deprecated **/
  get(...path: Array<string | number>): AbstractControl {
    const [pathEl] = path;
    if (pathEl && pathEl.toString().includes('.')) {
      throw Error(`Path string cannot include dot character: ${pathEl}`);
    }
    return super.get(path);
  }
}

export class TypeSafeFormControl<T extends number | string | boolean | moment.Moment> extends UntypedFormControl {

  readonly value: T;

  readonly valueChanges: Observable<T>;

  setValue(value: T, options?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.setValue(value, options);
  }

  patchValue(value: T, options?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.patchValue(value, options);
  }

  reset(value?: T, options?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.reset(value, options);
  }

  /** @deprecated **/
  get(path: Array<string | number> | string): AbstractControl | null {
    return super.get(path);
  }
}
